
import { supabase } from '@/integrations/supabase/client';
import { MaterialLabor } from '@/types/project';

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const ensureValidUuid = (id?: string): string => {
  if (id && UUID_REGEX.test(id)) return id;
  return crypto.randomUUID();
};

// Save materials to database with proper ordering using sequence numbers
export const saveMaterials = async (projectId: string, materials: MaterialLabor[]): Promise<boolean> => {
  try {
    
    if (!Array.isArray(materials) || materials.length === 0) {
      console.log('No materials to save');
      return true;
    }
    
    const materialsToInsert = materials.map((material, index) => ({
      id: ensureValidUuid(material.id),
      project_id: projectId,
      name: material.name,
      description: material.name,
      quantity: material.quantity,
      unit: material.unit,
      unit_price: material.unitPrice || 0,
      value_percentage: material.valuePercentage,
      start_date: material.startDate || null,
      end_date: material.endDate || null,
      sort_order: index + 1,
      remark: material.remark || null,
      change_name: material.changeName || null,
      change_quantity: material.changeQuantity ?? null,
      change_unit: material.changeUnit || null,
      change_remark: material.changeRemark || null,
      updated_at: new Date().toISOString()
    }));
    
    console.log('Inserting materials:', materialsToInsert);
    
    const { data, error } = await supabase
      .from('materials')
      .insert(materialsToInsert)
      .select();
    
    if (error) {
      console.error('Error saving materials:', error);
      return false;
    }
    
    console.log('Successfully saved materials:', data);
    return true;
  } catch (error) {
    console.error('Error saving materials:', error);
    return false;
  }
};

// Delete all materials for a project
export const deleteProjectMaterials = async (projectId: string): Promise<boolean> => {
  try {
    const { error } = await supabase
      .from('materials')
      .delete()
      .eq('project_id', projectId);
    
    if (error) {
      console.error('Error deleting materials for project:', projectId, error);
      return false;
    }
    
    return true;
  } catch (error) {
    console.error('Error deleting materials:', error);
    return false;
  }
};

// Save labor to database with proper ordering using sequence numbers
export const saveLabor = async (projectId: string, labor: MaterialLabor[]): Promise<boolean> => {
  try {
    
    if (!Array.isArray(labor) || labor.length === 0) {
      console.log('No labor to save');
      return true;
    }
    
    const laborToInsert = labor.map((laborItem, index) => ({
      id: ensureValidUuid(laborItem.id),
      project_id: projectId,
      name: laborItem.name,
      description: laborItem.name,
      quantity: laborItem.quantity,
      unit: laborItem.unit,
      unit_price: laborItem.unitPrice || 0,
      value_percentage: laborItem.valuePercentage,
      start_date: laborItem.startDate || null,
      end_date: laborItem.endDate || null,
      sort_order: index + 1,
      remark: laborItem.remark || null,
      change_name: laborItem.changeName || null,
      change_quantity: laborItem.changeQuantity ?? null,
      change_unit: laborItem.changeUnit || null,
      change_remark: laborItem.changeRemark || null,
      updated_at: new Date().toISOString()
    }));
    
    console.log('Inserting labor:', laborToInsert);
    
    const { data, error } = await supabase
      .from('labors')
      .insert(laborToInsert)
      .select();
    
    if (error) {
      console.error('Error saving labor:', error);
      return false;
    }
    
    console.log('Successfully saved labor:', data);
    return true;
  } catch (error) {
    console.error('Error saving labor:', error);
    return false;
  }
};

// Delete all labor for a project
export const deleteProjectLabor = async (projectId: string): Promise<boolean> => {
  try {
    const { error } = await supabase
      .from('labors')
      .delete()
      .eq('project_id', projectId);
    
    if (error) {
      console.error('Error deleting labor for project:', projectId, error);
      return false;
    }
    
    return true;
  } catch (error) {
    console.error('Error deleting labor:', error);
    return false;
  }
};
